import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import DashboardLayout from "../../components/DashboardLayout";
import DiploksWalletSummaryCard from "../../components/DiploksWalletSummaryCard";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import {
  CalendarRange, Users, Wallet, ArrowUpRight, Gavel, ScrollText,
} from "lucide-react";

export default function EventManagerDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [regs, setRegs] = useState([]);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setStats(r.data));
    api.get("/v2/events?limit=8").then((r) => setEvents(r.data.items || []));
    api.get("/v2/registrations?limit=10&sort=-registered_at").then((r) => setRegs(r.data.items || []));
  }, []);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Event Manager
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Let's run a conference, {user?.name?.split(" ")[0]}.
            </h1>
            <p className="mt-2 text-slate-500 text-sm">
              Pipeline, prep, and performance — your command deck.
            </p>
          </div>
          <Link to="/dashboard/events">
            <Button className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="em-manage-events-button">
              Manage events <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </motion.div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI icon={CalendarRange} label="Events" value={stats?.events} />
          <KPI icon={Users} label="Registrations" value={stats?.registrations} />
          <KPI icon={ScrollText} label="Papers" value={stats?.papers} />
          <KPI icon={Wallet} label="Revenue" value={`₹${(stats?.revenue || 0).toLocaleString()}`} gold />
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          <Card className="lg:col-span-7 bg-white border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold tracking-tight text-[#0A192F]">
                  Registrations per event
                </h3>
                <span className="text-xs text-emerald-600 font-semibold">LIVE</span>
              </div>
              <div className="mt-5 h-72 min-h-[280px]">
                {stats?.per_event?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={stats.per_event}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="event" tick={{ fontSize: 11, fill: "#64748b" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                      <Tooltip contentStyle={{ background: "#0A192F", border: "1px solid #D4AF37", color: "#fff" }} />
                      <Bar dataKey="registrations" radius={[6, 6, 0, 0]}>
                        {stats.per_event.map((_, i) => (
                          <Cell key={i} fill={i % 2 === 0 ? "#0F4C81" : "#D4AF37"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-slate-400">
                    Loading chart…
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="lg:col-span-5 space-y-6">
            <DiploksWalletSummaryCard />
            <Card className="bg-white border-slate-200">
              <CardContent className="p-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                  Latest registrations
                </p>
                <div className="mt-4 divide-y divide-slate-100">
                  {regs.slice(0, 5).map((r) => (
                    <div key={r.id} className="py-3 flex items-center justify-between" data-testid={`em-reg-${r.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-[#0A192F]">{r.delegate_name}</p>
                        <p className="text-xs text-slate-500">{r.event_id?.replace("event-", "") || "—"}</p>
                      </div>
                      <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${r.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-[#D4AF37]/15 text-[#0A192F]"}`}>
                        {r.payment_status}
                      </span>
                    </div>
                  ))}
                  {regs.length === 0 && (
                    <p className="py-6 text-center text-slate-500 text-sm">No registrations yet.</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0A192F] text-white border-[#D4AF37]/30">
              <CardContent className="p-6">
                <Gavel className="h-5 w-5 text-[#D4AF37]" />
                <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                  Events you run
                </p>
                <div className="mt-4 space-y-2">
                  {events.slice(0, 4).map((ev) => (
                    <Link to={`/dashboard/events/${ev.id}`} key={ev.id}
                          className="flex items-center justify-between p-3 rounded-md bg-white/5 hover:bg-white/10 transition-colors" data-testid={`em-event-${ev.id}`}>
                      <div>
                        <p className="text-sm font-semibold">{ev.title}</p>
                        <p className="text-xs text-slate-400">{new Date(ev.start_date).toLocaleDateString()}</p>
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D4AF37]">
                        {ev.status}
                      </span>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function KPI({ icon: Icon, label, value, gold }) {
  return (
    <div className={`p-5 rounded-xl border ${gold ? "bg-[#0A192F] text-white border-[#D4AF37]/30" : "bg-white border-slate-200"}`}>
      <Icon className={`h-4 w-4 ${gold ? "text-[#D4AF37]" : "text-[#0F4C81]"}`} />
      <p className={`mt-4 text-[10px] uppercase tracking-[0.22em] font-bold ${gold ? "text-[#D4AF37]" : "text-slate-500"}`}>{label}</p>
      <p className={`mt-1 font-serif text-3xl ${gold ? "text-[#D4AF37]" : "text-[#0A192F]"}`}>{value ?? "—"}</p>
    </div>
  );
}
